const ASSET_CACHE_PREFIX = "situate-runtime-assets-v1";

export interface CachedAssetResult {
  response: Response;
  /** Whether `response` was served from Cache Storage rather than fetched over the network. */
  fromCache: boolean;
}

export interface FetchCachedAssetOptions {
  /**
   * Stable logical Cache Storage identity to use instead of `url`. Pass this
   * when `url` is a temporary signed URL (e.g. a floorplan image) whose
   * query string changes across requests but whose underlying content is
   * addressed by a durable identity (such as `deploymentId + floorplanId +
   * revision`). `url` itself is still used for the network fetch on a cache
   * miss; only the cache key changes. Never pass the signed URL (or any of
   * its query parameters) as the cache key.
   */
  cacheKey?: string;
}

/**
 * Fetches a static file resource (JSON, PNG, JPEG, SVG, etc.) through the
 * browser's built-in Cache Storage, so it survives reloads and full page
 * navigations without re-hitting the network.
 *
 * File resources are expected to be served from versioned URLs (e.g.
 * `${scheme}://${host}/path?version=n`), so the full request URL - including
 * the query string - is used as the cache key: an unchanged version is served
 * straight from cache, while a bumped version is a cache miss that is fetched
 * once and stored under its own key. Any other previously cached response for
 * the same path (a stale version) is evicted so the cache doesn't grow
 * unbounded as assets are republished.
 *
 * Callers whose network address is a temporary signed URL (rather than a
 * stable versioned path) should pass `options.cacheKey`: a stable same-origin
 * key encoding the asset's durable identity (e.g. `deploymentId +
 * floorplanId + revision`) with the revision as its query string, so this
 * function's existing path-based stale-version eviction keeps working
 * unmodified.
 *
 * Falls back to a plain `fetch` when Cache Storage isn't available (e.g. in
 * some test environments or non-secure contexts), so callers never need to
 * feature-detect this themselves. `fromCache` tells callers (e.g.
 * `useCachedJsonAsset`) whether the response came straight from the network.
 */
export async function fetchCachedAsset(
  deploymentId: string,
  url: string,
  options: FetchCachedAssetOptions = {}
): Promise<CachedAssetResult> {
  if (typeof caches === "undefined") {
    return { response: await fetch(url), fromCache: false };
  }

  const canonicalUrl = new URL(options.cacheKey ?? url, window.location.href).href;
  const cache = await caches.open(`${ASSET_CACHE_PREFIX}:${encodeURIComponent(deploymentId)}`);
  const cached = await cache.match(canonicalUrl);
  if (cached) {
    return { response: cached, fromCache: true };
  }

  const response = await fetch(url);
  if (response.ok) {
    await evictStaleVersions(cache, canonicalUrl);
    await cache.put(canonicalUrl, response.clone());
  }

  return { response, fromCache: false };
}

/** Removes any cache entry for the same path as `url` other than `url` itself. */
async function evictStaleVersions(cache: Cache, url: string): Promise<void> {
  const currentUrl = new URL(url);
  const requests = await cache.keys();

  await Promise.all(
    requests
      .filter((request) => {
        const requestUrl = new URL(request.url);
        return (
          requestUrl.href !== currentUrl.href &&
          requestUrl.origin === currentUrl.origin &&
          requestUrl.pathname === currentUrl.pathname
        );
      })
      .map((request) => cache.delete(request))
  );
}
